import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { inr, short, date, today } from '../lib/fmt';
import { Button, Card, Field, Input, Select, Modal, Note, PageHead, Pill, Tag,
         Tile, Tiles, Table, Th, Td, TableWrap, Empty } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton } from '../components/skeleton';

interface Bill {
  _id: string; name: string; amount: number; is_variable: boolean; auto_debit: boolean;
  active: boolean; next_due: string; day_of_month: number; cadence: string;
  vendor?: { _id: string; name: string } | null;
  category?: { _id: string; name: string } | null;
  pay_from?: { _id: string; name: string } | null;
}

/** Head, the four totals, what is due now, then every bill on file. */
function BillsSkeleton() {
  return (
    <Loading label="Loading recurring bills">
      <HeadSkeleton actions={1} />
      <TilesSkeleton />
      <CardSkeleton flush className="mb-5"><TableSkeleton rows={3} cols={5} /></CardSkeleton>
      <CardSkeleton flush head={false}><TableSkeleton rows={7} cols={6} /></CardSkeleton>
    </Loading>
  );
}

export default function Bills() {
  const { can, meta } = useSession();
  const feed = useAsync<{ bills: Bill[]; due: Bill[] }>(() => api.get('/api/bills', { within: 21 }), []);
  const vendors = useAsync<{ _id: string; name: string }[]>(() => api.get('/api/vendors'), []);
  const [edit, setEdit] = useState<Bill | 'new' | null>(null);
  const [pay, setPay] = useState<Bill | null>(null);

  if (feed.loading) return <BillsSkeleton />;
  if (feed.error) return <Note title="That did not load" tone="critical">{feed.error}</Note>;
  const d = feed.data!;
  const monthly = d.bills.filter((b) => b.active).reduce((s, b) => s + b.amount, 0);

  return (
    <div>
      <PageHead eyebrow="Money out" title="Recurring bills"
        dek="Rent, electricity, internet, pantry — the payments that arrive every month whether anyone remembers them or not. These drive the “due now” queue and the cash projection."
        actions={can('vendor:manage') && <Button variant="primary" onClick={() => setEdit('new')}>Add a bill</Button>} />

      <Tiles>
        <Tile hero label="Every month" value={short(monthly)}
              sub={<span className="text-muted">{d.bills.filter((b) => b.active).length} active bills</span>} />
        <Tile label="Due in 3 weeks" value={String(d.due.length)}
              sub={<span className="text-muted">
                {d.due.length ? `${short(d.due.reduce((s, b) => s + b.amount, 0))} in value` : 'nothing pending'}</span>} />
        <Tile label="Variable amounts" value={String(d.bills.filter((b) => b.is_variable).length)}
              sub={<span className="text-muted">electricity and similar — the figure is a typical one</span>} />
        <Tile label="On auto-debit" value={String(d.bills.filter((b) => b.auto_debit).length)}
              sub={<span className="text-muted">leave the bank on their own</span>} />
      </Tiles>

      {d.due.length > 0 && (
        <Card title="Due now" sub="Recording a payment here moves the money and rolls the bill to next month."
              className="mb-5" flush>
          <TableWrap>
            <Table cards>
              <thead><tr><Th>Bill</Th><Th>Due</Th><Th>Pay from</Th><Th n>Amount</Th><Th /></tr></thead>
              <tbody>
                {d.due.map((b) => (
                  <tr key={b._id}>
                    <Td label="Bill"><b>{b.name}</b>{b.vendor && <span className="text-muted"> · {b.vendor.name}</span>}</Td>
                    <Td label="Due" className={`font-mono text-[11.5px] ${b.next_due <= today() ? 'text-critical' : ''}`}>
                      {date(b.next_due)}</Td>
                    <Td label="Pay from" className="text-ink-2">{b.pay_from?.name ?? '—'}</Td>
                    <Td label="Amount" n>{inr(b.amount)}{b.is_variable && <> <Tag>approx</Tag></>}</Td>
                    <Td label="" className="text-right">
                      {can('expense:pay') && <Button size="sm" variant="primary" onClick={() => setPay(b)}>Record payment</Button>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}

      <Card flush>
        {!d.bills.length ? <Empty>No recurring bills set up yet.</Empty> : (
          <TableWrap>
            <Table cards>
              <thead><tr>
                <Th>Bill</Th><Th>Vendor</Th><Th>Category</Th><Th n>Typical</Th>
                <Th>Next due</Th><Th>Pay from</Th><Th>State</Th><Th />
              </tr></thead>
              <tbody>
                {d.bills.map((b) => (
                  <tr key={b._id} className={b.active ? '' : 'opacity-55'}>
                    <Td label="Bill"><b>{b.name}</b></Td>
                    <Td label="Vendor" className="text-ink-2">{b.vendor?.name ?? '—'}</Td>
                    <Td label="Category" className="text-ink-2">{b.category?.name ?? '—'}</Td>
                    <Td label="Typical" n>{inr(b.amount)}</Td>
                    <Td label="Next due" className="font-mono text-[11.5px]">{date(b.next_due)}</Td>
                    <Td label="Pay from" className="text-ink-2">{b.pay_from?.name ?? '—'}</Td>
                    <Td label="State">
                      <Pill kind={b.active ? 'active' : 'draft'}>{b.active ? 'active' : 'paused'}</Pill>
                      {b.auto_debit && <> <Tag>auto</Tag></>}
                    </Td>
                    <Td label="" className="text-right">
                      {can('vendor:manage') && <Button size="sm" variant="ghost" onClick={() => setEdit(b)}>Edit</Button>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {edit && <BillModal bill={edit === 'new' ? null : edit}
                          vendors={vendors.data ?? []} categories={meta?.categories ?? []}
                          accounts={meta?.accounts ?? []}
                          onClose={() => setEdit(null)} onDone={() => { setEdit(null); feed.reload(); }} />}
      {pay && <PayModal bill={pay} accounts={meta?.accounts ?? []}
                        onClose={() => setPay(null)} onDone={() => { setPay(null); feed.reload(); }} />}
    </div>
  );
}

function BillModal(
  { bill, vendors, categories, accounts, onClose, onDone }:
  { bill: Bill | null; vendors: { _id: string; name: string }[];
    categories: { _id: string; name: string }[]; accounts: { id: string; name: string }[];
    onClose: () => void; onDone: () => void },
) {
  const [f, setF] = useState({
    name: bill?.name ?? '',
    vendor: bill?.vendor?._id ?? '',
    category: bill?.category?._id ?? categories[0]?._id ?? '',
    amount: bill ? (bill.amount / 100).toFixed(2) : '',
    day_of_month: String(bill?.day_of_month ?? 5),
    next_due: bill?.next_due ?? today(),
    pay_from: bill?.pay_from?._id ?? accounts[0]?.id ?? '',
  });
  const [isVariable, setIsVariable] = useState(bill?.is_variable ?? false);
  const [autoDebit, setAutoDebit] = useState(bill?.auto_debit ?? false);
  const [active, setActive] = useState(bill?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  return (
    <Modal title={bill ? `Edit ${bill.name}` : 'Add a recurring bill'} wide onClose={onClose}
      confirmLabel={bill ? 'Save' : 'Add bill'} busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.post('/api/bills', {
            id: bill?._id, ...f,
            vendor: f.vendor || null,
            amount: Math.round(Number(f.amount) * 100),
            day_of_month: Number(f.day_of_month),
            is_variable: isVariable, auto_debit: autoDebit, active, cadence: 'monthly',
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Name"><Input value={f.name} onChange={set('name')} /></Field>
        <Field label="Vendor">
          <Select value={f.vendor} onChange={set('vendor')}>
            <option value="">No vendor</option>
            {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
          </Select>
        </Field>
        <Field label="Category">
          <Select value={f.category} onChange={set('category')}>
            {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Typical amount (₹)"><Input type="number" step="0.01" value={f.amount} onChange={set('amount')} /></Field>
        <Field label="Day of month"><Input type="number" min="1" max="28" value={f.day_of_month} onChange={set('day_of_month')} /></Field>
        <Field label="Next due"><Input type="date" value={f.next_due} onChange={set('next_due')} /></Field>
        <Field label="Pay from">
          <Select value={f.pay_from} onChange={set('pay_from')}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <div className="flex flex-col gap-2 justify-end text-[13.5px] text-ink-2">
          <label className="flex items-center gap-2"><input type="checkbox" className="w-4 h-4"
            checked={isVariable} onChange={(e) => setIsVariable(e.target.checked)} />Amount varies</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="w-4 h-4"
            checked={autoDebit} onChange={(e) => setAutoDebit(e.target.checked)} />Auto-debit</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="w-4 h-4"
            checked={active} onChange={(e) => setActive(e.target.checked)} />Active</label>
        </div>
      </div>
      {err && <p className="mt-3 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}

function PayModal(
  { bill, accounts, onClose, onDone }:
  { bill: Bill; accounts: { id: string; name: string }[]; onClose: () => void; onDone: () => void },
) {
  const [amount, setAmount] = useState((bill.amount / 100).toFixed(2));
  const [when, setWhen] = useState(today());
  const [account, setAccount] = useState(bill.pay_from?._id ?? accounts[0]?.id ?? '');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  return (
    <Modal title={`Record ${bill.name}`} onClose={onClose} confirmLabel="Record payment" busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.post(`/api/bills/${bill._id}/pay`, {
            amount: Math.round(Number(amount) * 100), paid_on: when,
            pay_from: account, reference: reference.trim() || null,
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      {bill.is_variable && (
        <p className="text-[13px] text-ink-2 mb-3.5">This bill varies — enter what was actually charged.</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Amount (₹)"><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Field label="Paid on"><Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
        <Field label="From">
          <Select value={account} onChange={(e) => setAccount(e.target.value)}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="Reference"><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR" /></Field>
      </div>
      {err && <p className="mt-3 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}
